import { type Issue, getIssueKind, getIssueTarget } from '../lib/github-client'
import { t, type Lang } from '../lib/i18n'

interface RequestCardProps {
  issue: Issue
  lang: Lang
}

const KIND_BADGE: Record<'request' | 'feedback', string> = {
  request: 'bg-primary/10 text-primary-text',
  feedback: 'bg-success/10 text-success',
}

function bodyExcerpt(raw: string): string {
  // Strip markdown headings, links, code fences for compact preview.
  const cleaned = raw
    .replace(/^#+\s.*$/gm, '')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/<sub>[\s\S]*?<\/sub>/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
  return cleaned.length > 240 ? cleaned.slice(0, 237) + '…' : cleaned
}

function timeAgo(iso: string, lang: Lang): string {
  const diffMs = Date.now() - Date.parse(iso)
  const sec = Math.max(0, Math.floor(diffMs / 1000))
  const min = Math.floor(sec / 60)
  const hr = Math.floor(min / 60)
  const day = Math.floor(hr / 24)
  if (lang === 'vi') {
    if (day > 0) return `${day} ngày trước`
    if (hr > 0) return `${hr} giờ trước`
    if (min > 0) return `${min} phút trước`
    return 'vừa xong'
  }
  if (day > 0) return `${day}d ago`
  if (hr > 0) return `${hr}h ago`
  if (min > 0) return `${min}m ago`
  return 'just now'
}

export function RequestCard({ issue, lang }: RequestCardProps) {
  const kind = getIssueKind(issue)
  const target = getIssueTarget(issue)
  const reactions = issue.reactions.total_count
  const upvotes = issue.reactions['+1']

  return (
    <article className="group rounded-lg border border-border bg-bg-elevated p-5 transition-colors hover:border-border-strong">
      <div className="flex items-start gap-3">
        <a
          href={issue.html_url}
          target="_blank"
          rel="noopener"
          title={t(lang, 'requests.card.upvote')}
          className="flex shrink-0 flex-col items-center gap-0.5 rounded-md border border-border bg-bg-muted px-2 py-1.5 text-text-muted transition-colors hover:border-primary hover:text-primary-text"
        >
          <span className="text-base leading-none">▲</span>
          <span className="text-xs font-semibold tabular-nums">{upvotes || reactions}</span>
        </a>

        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
            {kind && (
              <span className={`rounded-md px-1.5 py-0.5 text-xs font-medium ${KIND_BADGE[kind]}`}>
                {t(lang, `requests.filter.kind.${kind}` as const)}
              </span>
            )}
            {target && target !== 'all' && (
              <span className="rounded-md bg-bg-muted px-1.5 py-0.5 text-xs text-text-muted">
                {target}
              </span>
            )}
            <span className="ml-auto text-xs text-text-subtle">
              {timeAgo(issue.created_at, lang)}
            </span>
          </div>

          <h3 className="text-base font-semibold leading-snug text-text">
            <a
              href={issue.html_url}
              target="_blank"
              rel="noopener"
              className="hover:text-primary-text"
            >
              {issue.title}
            </a>
          </h3>

          <p className="mt-1.5 line-clamp-3 text-sm text-text-muted">
            {bodyExcerpt(issue.body || '')}
          </p>

          <div className="mt-3 flex items-center gap-3 text-xs text-text-subtle">
            <a
              href={issue.html_url}
              target="_blank"
              rel="noopener"
              className="underline-offset-4 hover:text-primary-text hover:underline"
            >
              {t(lang, 'requests.card.viewIssue')} #{issue.number} →
            </a>
            {issue.state === 'closed' && (
              <span className="rounded bg-bg-muted px-1.5 py-0.5">closed</span>
            )}
          </div>
        </div>
      </div>
    </article>
  )
}
